import { LANGUAGE_CODES, type Language } from '@hero/schema';
import { arPack } from './ar';
import { enPack } from './en';
import { urPack } from './ur';
import type { LanguagePack } from './types';

/**
 * Every active language, keyed by code. Readers (detection, the reply
 * script guard, the picker, dates, translation targets, the composer)
 * go through this — never a hand-written `['ar', 'en', 'ur']`.
 */
export const languageRegistry: { readonly [Code in Language]: LanguagePack<Code> } = {
  ar: arPack,
  en: enPack,
  ur: urPack,
};

/** Active codes in picker order. */
export const ACTIVE_LANGUAGE_CODES: readonly Language[] = LANGUAGE_CODES;

export const activeLanguagePacks = (): LanguagePack<Language>[] =>
  ACTIVE_LANGUAGE_CODES.map(code => languageRegistry[code]);

export const getLanguagePack = (code: Language): LanguagePack<Language> => languageRegistry[code];

export const isActiveLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (ACTIVE_LANGUAGE_CODES as readonly string[]).includes(value);

/** What an STT engine reported (`urd`, `arb`, `en`) → a language, or `undefined` when no pack claims it. */
export const languageForSttCode = (
  code: string | undefined,
  packs: readonly LanguagePack[] = activeLanguagePacks(),
): string | undefined => {
  const normalized = code?.trim().toLowerCase();
  if (!normalized) return undefined;
  return packs.find(pack => pack.detection.sttCodes.includes(normalized))?.code;
};

/** First pack whose explicit-switch phrase matches, in registry order. */
export const matchExplicitSwitch = (
  text: string,
  packs: readonly LanguagePack[] = activeLanguagePacks(),
): string | null => {
  for (const pack of packs) {
    if (pack.detection.explicitSwitchPhrases.some(re => re.test(text))) return pack.code;
  }
  return null;
};

const greetingKey = (text: string): string =>
  text
    .normalize('NFC')
    .replace(/[\u064B-\u0652\u0640]/g, '')
    .replace(/[\p{P}\p{S}\p{Extended_Pictographic}\uFE0F\u200d]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * The pack whose greeting the whole message is («هلا», «الو», Urdu-script
 * «السلام علیکم»), when only one pack owns it.
 *
 * A Latin greeting never counts: «Hello» / «hi» is how Arabic and Urdu
 * speakers open a WhatsApp chat too, so it says nothing about the reply
 * language and the picker still asks.
 */
export const matchGreeting = (
  text: string,
  packs: readonly LanguagePack[] = activeLanguagePacks(),
): string | null => {
  const key = greetingKey(text);
  if (!key || /[a-z]/.test(key)) return null;
  const owners = packs.filter(pack =>
    pack.detection.greetingWords.some(word => greetingKey(word) === key),
  );
  return owners.length === 1 ? owners[0].code : null;
};

/** Letters only this pack's language uses within its script, as a regex; `null` when it has none. */
export const distinctiveCharsRegex = (pack: LanguagePack): RegExp | null =>
  pack.detection.distinctiveChars ? new RegExp(`[${pack.detection.distinctiveChars}]`, 'u') : null;

/** Dates in the pack's locale — Gregorian calendar, Western digits. */
export const formatDateForLanguage = (
  date: Date,
  language: Language,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' },
): string => new Intl.DateTimeFormat(getLanguagePack(language).locales.date, options).format(date);

/** `1–2 أكتوبر` / `30 September – 2 October` — the shared month is said once. */
export const formatDateRangeForLanguage = (
  from: Date,
  to: Date,
  language: Language,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' },
): string => {
  // `formatRange` is ES2021; the lib target here predates it, Node has it.
  const format = new Intl.DateTimeFormat(getLanguagePack(language).locales.date, options) as Intl.DateTimeFormat & {
    formatRange(start: Date, end: Date): string;
  };
  return format.formatRange(from, to);
};

/** WhatsApp shows at most three reply buttons; more options need a list message. */
export const PICKER_MAX_BUTTONS = 3;

export type LanguagePickerLayout =
  | { kind: 'buttons'; options: Array<{ code: string; title: string }> }
  | { kind: 'list'; options: Array<{ code: string; title: string; description: string }> };

/** Reply buttons for up to three languages, a list message beyond. Titles in each language's own script. */
export const languagePickerLayout = (
  packs: readonly LanguagePack[] = activeLanguagePacks(),
): LanguagePickerLayout =>
  packs.length <= PICKER_MAX_BUTTONS
    ? { kind: 'buttons', options: packs.map(pack => ({ code: pack.code, title: pack.name.native })) }
    : {
        kind: 'list',
        options: packs.map(pack => ({
          code: pack.code,
          title: pack.name.native,
          description: pack.name.english,
        })),
      };
